'use client'

import { Address } from '@/feature/address/components/address/Address.client'
import { useStoreLocatorUrl } from '@/feature/store-locator'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Icon } from '@/ui/icons/icon/Icon'
import { gql } from '@gql-generated/gql'
import type { DisplayPointOfSaleInfos_PointOfSaleFragment } from '@gql-generated/graphql'
import { useCallback } from 'react'
import { buildGoogleMapsRequest } from '../../../../services/pointOfSale.formatting'
import { PointOfSaleOpeningHoursList } from '../../../point-of-sale-opening-hours-list/PointOfSaleOpeningHoursList'
import { PointOfSaleExceptionalDatesAlert } from '../../_components/PointOfSaleExceptionalDatesAlert'

import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { createOpenExternalWebUrlIntent } from '@/lib/react-native/services/react-native.web-view'
import { Link } from '@/ui/links/link/Link.client'

gql(/* GraphQL */ `
  fragment DisplayPointOfSaleInfos_pointOfSale on PointOfSale {
    id
    name
    link
    distributionNetworkLabel
    address {
      city
      line1
      line2
      line3
      postalCode
      isoCountryCode
    }
    phoneNumber
    exceptionalDates {
      date
      intervals {
        begin
        end
      }
      isClosed
    }
    openingHours {
      day
      dayOfWeek
      intervals {
        begin
        end
      }
    }
    subsidiary {
      name
    }
  }
`)

type DisplayPointOfSaleInfosProps = {
  infos: DisplayPointOfSaleInfos_PointOfSaleFragment | null | undefined
}

export function DisplayPointOfSaleInfos({
  infos,
}: DisplayPointOfSaleInfosProps): React.ReactElement {
  const { address, phoneNumber, exceptionalDates, openingHours, link } = infos || {}
  const { t } = useTranslation(['pointOfSale'])
  const storeLocatorUrl = useStoreLocatorUrl()

  const pushToDataLayerPointOfSale = useCallback((label: string) => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'header',
      eventAction: 'drawer magasin',
      eventLabel: label,
    })
  }, [])

  return (
    <>
      {address && address.city && address.line1 && address.postalCode && address.isoCountryCode && (
        <Address
          className="mb-3"
          city={address.city}
          line1={address.line1}
          line2={address.line2}
          line3={address.line3}
          postalCode={address.postalCode}
          isoCountryCode={address.isoCountryCode}
          phone={phoneNumber}
          onPhonePress={() => pushToDataLayerPointOfSale('Téléphone')}
        />
      )}
      <div className="mb-3 flex flex-col flex-wrap gap-1 tablet:flex-row">
        {phoneNumber && (
          <Link
            bold
            href={`tel:${phoneNumber}`}
            variant="button-secondary"
            size="lg"
            iconLeft={<Icon.Phone />}
            className="flex tablet:flex-1"
            onPress={() => pushToDataLayerPointOfSale('Contactez-nous')}
            intent={INTENT_SAME_AS_WEB}
          >
            {t('pointOfSale:contactUs')}
          </Link>
        )}
        {infos && (
          <Link
            bold
            variant="button-secondary"
            iconLeft={<Icon.MapPin />}
            size="lg"
            href={buildGoogleMapsRequest(infos)}
            className="flex tablet:flex-1"
            opensInNewTab
            onPress={() => pushToDataLayerPointOfSale('Itinéraire')}
            intent={INTENT_SAME_AS_WEB}
          >
            {t('pointOfSale:itinerary')}
          </Link>
        )}
      </div>
      {!!openingHours?.length && (
        <PointOfSaleOpeningHoursList
          className="mb-1"
          dayOpeningHours={openingHours}
          exceptionalDates={exceptionalDates || []}
        />
      )}
      {storeLocatorUrl && link && (
        <Link
          bold
          className="mb-3 inline-block"
          href={link}
          opensInNewTab
          onPress={() => pushToDataLayerPointOfSale('En savoir plus')}
          intent={createOpenExternalWebUrlIntent(link)}
        >
          {t('pointOfSale:moreInfos')}
        </Link>
      )}
      {!!exceptionalDates?.length && (
        <PointOfSaleExceptionalDatesAlert exceptionalDates={exceptionalDates} className="mt-2" />
      )}
    </>
  )
}
